import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid2 as Grid,
  Chip,
  Alert,
  Paper,
  Divider,
  Tooltip,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Build,
  Add,
  Delete,
  Edit,
  Save,
  Cancel,
  ExpandMore,
  DragIndicator,
  Warning,
  CheckCircle,
  Group,
  AccountBalance,
  Engineering,
  ArrowBack,
  ElectricalServices,
  Plumbing,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

const PpEditInspectionsPage = () => {
  const { permitId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [permit, setPermit] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [associatedPermits, setAssociatedPermits] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedPermitForAdd, setSelectedPermitForAdd] = useState("");
  const [saving, setSaving] = useState(false);

  // Mock permit data with associated permits
  const mockPermit = {
    id: permitId,
    number: "PKG2024001",
    address: "123 Oak Street, Jacksonville, FL 32207",
    type: "Building Permit",
    status: "active",
    associatedPermits: [
      {
        id: "MECH001",
        type: "Mechanical",
        status: "active",
        icon: <Engineering />,
        color: "info",
        inspections: [
          {
            id: "M001",
            name: "Mechanical Rough-In",
            required: true,
            status: "not_scheduled",
            canEdit: true,
          },
          {
            id: "M002",
            name: "Mechanical Final",
            required: true,
            status: "not_scheduled",
            canEdit: true,
          },
        ],
      },
      {
        id: "ELEC001",
        type: "Electrical",
        status: "active",
        icon: <ElectricalServices />,
        color: "warning",
        inspections: [
          {
            id: "E001",
            name: "Electrical Rough-In",
            required: true,
            status: "not_scheduled",
            canEdit: true,
          },
          {
            id: "E002",
            name: "Electrical Final",
            required: true,
            status: "not_scheduled",
            canEdit: true,
          },
        ],
      },
      {
        id: "PLUMB001",
        type: "Plumbing",
        status: "active",
        icon: <Plumbing />,
        color: "primary",
        inspections: [
          {
            id: "P001",
            name: "Plumbing Rough-In",
            required: true,
            status: "not_scheduled",
            canEdit: true,
          },
          {
            id: "P002",
            name: "Plumbing Final",
            required: true,
            status: "not_scheduled",
            canEdit: true,
          },
        ],
      },
    ],
  };

  // Main building permit inspections
  const mockInspections = [
    {
      id: "B001",
      name: "Foundation",
      required: true,
      status: "passed",
      canEdit: false,
      order: 1,
    },
    {
      id: "B002",
      name: "Framing",
      required: true,
      status: "not_scheduled",
      canEdit: true,
      order: 2,
    },
    {
      id: "B003",
      name: "Insulation",
      required: true,
      status: "not_scheduled",
      canEdit: true,
      order: 3,
    },
    {
      id: "B004",
      name: "Drywall",
      required: false,
      status: "not_scheduled",
      canEdit: true,
      order: 4,
    },
    {
      id: "B005",
      name: "Final Building",
      required: true,
      status: "not_scheduled",
      canEdit: true,
      order: 5,
    },
  ];

  // Available inspections that can be added
  const availableInspections = [
    { id: "A001", name: "Footing", permitType: "building" },
    { id: "A002", name: "Slab", permitType: "building" },
    { id: "A003", name: "Roof", permitType: "building" },
    { id: "A004", name: "Pool/Spa", permitType: "building" },
    { id: "A005", name: "HVAC Equipment", permitType: "mechanical" },
    { id: "A006", name: "Ductwork", permitType: "mechanical" },
    { id: "A007", name: "Service Panel", permitType: "electrical" },
    { id: "A008", name: "Outlets & Switches", permitType: "electrical" },
    { id: "A009", name: "Water Heater", permitType: "plumbing" },
    { id: "A010", name: "Fixtures", permitType: "plumbing" },
  ];

  useEffect(() => {
    setPermit(mockPermit);
    setInspections(mockInspections);
    setAssociatedPermits(mockPermit.associatedPermits);
  }, [permitId]);

  const handleSaveInspection = async () => {
    if (!selectedInspection) return;

    try {
      setSaving(true);

      // Mock save operation
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Update inspection in the appropriate list
      if (selectedInspection.permitType === "building") {
        setInspections((prev) =>
          prev.map((inspection) =>
            inspection.id === selectedInspection.id
              ? selectedInspection
              : inspection
          )
        );
      } else {
        setAssociatedPermits((prev) =>
          prev.map((permit) => ({
            ...permit,
            inspections: permit.inspections.map((inspection) =>
              inspection.id === selectedInspection.id
                ? selectedInspection
                : inspection
            ),
          }))
        );
      }

      setEditDialogOpen(false);
      setSelectedInspection(null);
    } catch (error) {
      console.error("Failed to save inspection:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInspection = async (inspection) => {
    if (!inspection.canEdit) {
      alert("Cannot delete inspection that has been reported to the city");
      return;
    }

    try {
      // Remove from appropriate list
      if (inspection.permitType === "building") {
        setInspections((prev) => prev.filter((i) => i.id !== inspection.id));
      } else {
        setAssociatedPermits((prev) =>
          prev.map((permit) => ({
            ...permit,
            inspections: permit.inspections.filter(
              (i) => i.id !== inspection.id
            ),
          }))
        );
      }
    } catch (error) {
      console.error("Failed to delete inspection:", error);
    }
  };

  const handleAddInspection = async () => {
    const permitType = selectedPermitForAdd || "building";
    const newInspection = {
      id: `NEW${Date.now()}`,
      name: "New Inspection",
      required: true,
      status: "not_scheduled",
      canEdit: true,
      permitType: permitType,
      order: getNextOrder(permitType),
    };

    try {
      if (permitType === "building") {
        setInspections((prev) => [...prev, newInspection]);
      } else {
        setAssociatedPermits((prev) =>
          prev.map((permit) =>
            permit.id.toLowerCase().includes(permitType.toLowerCase())
              ? {
                  ...permit,
                  inspections: [...permit.inspections, newInspection],
                }
              : permit
          )
        );
      }

      setSelectedInspection(newInspection);
      setEditDialogOpen(true);
      setAddDialogOpen(false);
    } catch (error) {
      console.error("Failed to add inspection:", error);
    }
  };

  const getNextOrder = (permitType) => {
    if (permitType === "building") {
      return Math.max(...inspections.map((i) => i.order || 0)) + 1;
    } else {
      const permit = associatedPermits.find((p) =>
        p.id.toLowerCase().includes(permitType.toLowerCase())
      );
      return permit
        ? Math.max(...permit.inspections.map((i) => i.order || 0)) + 1
        : 1;
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      not_scheduled: { color: "default", label: "Not Scheduled" },
      pending: { color: "warning", label: "Pending" },
      approved: { color: "primary", label: "Approved" },
      reported: { color: "secondary", label: "Reported" },
      passed: { color: "success", label: "Passed" },
      failed: { color: "error", label: "Failed" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const InspectionsList = ({ title, inspections, permitType, icon, color }) => (
    <Card sx={{ mb: 2 }}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            {icon}
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              {title} ({inspections.length})
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                setSelectedPermitForAdd(permitType);
                setAddDialogOpen(true);
              }}
            >
              Add
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {inspections.length === 0 ? (
            <Alert severity="info">
              No inspections defined for this permit type yet.
            </Alert>
          ) : (
            <List>
              {inspections
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((inspection, index) => (
                  <ListItem
                    key={inspection.id}
                    divider
                    sx={{
                      bgcolor: !inspection.canEdit ? "grey.100" : "transparent",
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemIcon>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Chip
                          label={inspection.order || index + 1}
                          size="small"
                          color={color}
                        />
                        {inspection.canEdit && (
                          <IconButton size="small" sx={{ mt: 0.5 }}>
                            <DragIndicator fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography variant="subtitle2">
                            {inspection.name}
                          </Typography>
                          {inspection.required ? (
                            <Chip
                              label="Required"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Optional"
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {getStatusChip(inspection.status)}
                          {!inspection.canEdit && (
                            <Chip
                              label="Reported"
                              color="secondary"
                              size="small"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        !inspection.canEdit
                          ? "Cannot edit - already reported to city"
                          : `Order: ${inspection.order || index + 1}`
                      }
                    />

                    <ListItemSecondaryAction>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {inspection.canEdit && (
                          <>
                            <Tooltip title="Edit Inspection">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedInspection({
                                    ...inspection,
                                    permitType,
                                  });
                                  setEditDialogOpen(true);
                                }}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Inspection">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDeleteInspection({
                                    ...inspection,
                                    permitType,
                                  })
                                }
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          )}
        </AccordionDetails>
      </Accordion>
    </Card>
  );

  if (!permit) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Permit not found.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: isMobile ? 2 : 3,
        pb: isMobile ? "calc(30px + 30px)" : "calc(30px + 80px)",
      }}
    >
      {/* Header with back arrow */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(`/pp/inspections/${permitId}`)}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography color="primary" variant="h4">
            Edit Inspections - {permit.number}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {permit.address}
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ my: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> You can add/remove inspections until they are
          reported to the city. Once reported, inspections cannot be modified.
          Duplicate passed inspections are not allowed.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Main Building Permit */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <InspectionsList
            title="Building Permit"
            inspections={inspections}
            permitType="building"
            icon={<AccountBalance />}
            color="primary"
          />
        </Grid>

        {/* Associated Trade Permits */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Group />
              Associated Trade Permits
            </Typography>

            {associatedPermits.map((associatedPermit) => (
              <InspectionsList
                key={associatedPermit.id}
                title={`${associatedPermit.type} Permit`}
                inspections={associatedPermit.inspections}
                permitType={associatedPermit.type.toLowerCase()}
                icon={associatedPermit.icon}
                color={associatedPermit.color}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      {/* Edit Inspection Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedInspection?.id?.includes("NEW")
            ? "Add New Inspection"
            : "Edit Inspection"}
        </DialogTitle>
        <DialogContent>
          {selectedInspection && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Inspection Name"
                  value={selectedInspection.name}
                  onChange={(e) =>
                    setSelectedInspection((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Inspection Type</InputLabel>
                  <Select
                    value={selectedInspection.permitType || "building"}
                    label="Inspection Type"
                    onChange={(e) =>
                      setSelectedInspection((prev) => ({
                        ...prev,
                        permitType: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="building">Building Permit</MenuItem>
                    <MenuItem value="mechanical">Mechanical Permit</MenuItem>
                    <MenuItem value="electrical">Electrical Permit</MenuItem>
                    <MenuItem value="plumbing">Plumbing Permit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedInspection.required}
                      onChange={(e) =>
                        setSelectedInspection((prev) => ({
                          ...prev,
                          required: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Required Inspection"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Order"
                  value={selectedInspection.order || 1}
                  onChange={(e) =>
                    setSelectedInspection((prev) => ({
                      ...prev,
                      order: parseInt(e.target.value),
                    }))
                  }
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    <strong>Reminder:</strong> Once this inspection is reported
                    to the city, it cannot be edited or removed.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveInspection}
            disabled={!selectedInspection?.name || saving}
            startIcon={<Save />}
          >
            {saving ? "Saving..." : "Save Inspection"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Inspection Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Inspection</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Permit Type</InputLabel>
                <Select
                  value={selectedPermitForAdd}
                  label="Permit Type"
                  onChange={(e) => setSelectedPermitForAdd(e.target.value)}
                >
                  <MenuItem value="building">Building Permit</MenuItem>
                  <MenuItem value="mechanical">Mechanical Permit</MenuItem>
                  <MenuItem value="electrical">Electrical Permit</MenuItem>
                  <MenuItem value="plumbing">Plumbing Permit</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom>
                Available Inspections for {selectedPermitForAdd || "selected"}{" "}
                permit:
              </Typography>
              <List dense>
                {availableInspections
                  .filter((ai) => ai.permitType === selectedPermitForAdd)
                  .map((inspection) => (
                    <ListItem key={inspection.id} button>
                      <ListItemIcon>
                        <Build fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={inspection.name} />
                      <ListItemSecondaryAction>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            const newInsp = {
                              ...inspection,
                              required: true,
                              status: "not_scheduled",
                              canEdit: true,
                              permitType: selectedPermitForAdd,
                              order: getNextOrder(selectedPermitForAdd),
                            };
                            setSelectedInspection(newInsp);
                            setAddDialogOpen(false);
                            setEditDialogOpen(true);
                          }}
                        >
                          Add
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddInspection}
            disabled={!selectedPermitForAdd}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Buttons */}
      <Box
        sx={{
          position: "fixed",
          bottom: isMobile ? "calc(100px + 24px)" : "calc(80px + 24px)",
          right: 24,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 1,
          zIndex: 1000,
        }}
      >
        <Button variant="outlined" onClick={() => navigate("/pp/inspections")}>
          Back to Board
        </Button>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Inspection
        </Button>
      </Box>
    </Box>
  );
};

export default PpEditInspectionsPage;
